import React, { FC, useState, useEffect } from 'react';
import { DropdownComponent } from 'ReusableComponents/Dropdown/DropdownComponent';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { InputComponent } from 'ReusableComponents/Input/InputComponent';
import './BillingCsvReport.scss';
import { downloadFile } from 'utils/fetchDownload';
import { queryStringFromObject as queryString } from 'utils/urlUtil';
import { CircleProgressComponent } from 'ReusableComponents/Progress/ProgressComponent';

import dayjs from 'dayjs';
import localeDataPlugin from 'dayjs/plugin/localeData';

// allow for dayjs().localeData()
dayjs.extend(localeDataPlugin);

const dropdownMonths = (year: string) =>
  dayjs()
    .localeData()
    .months()
    .filter((month: string, i: number) =>
      year === dayjs().get('year').toString() ? dayjs().get('month') > i : month
    )
    .map((month: string, i: number) => ({ name: month, value: i }));

const FetchCsvButton: FC<{
  date: Date;
  disabled: boolean;
}> = ({ date, disabled }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchMonthCsv = async (date: Date): Promise<void> => {
    const startDate = dayjs(date);
    const endDate = startDate.add(1, 'month');

    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');

    const filename = `aggregated_billing_report_${start}_${end}.csv`;

    setIsLoading(true);
    await downloadFile(
      `reports/billing-csv/aggregated${queryString({ start, end })}`,
      filename
    );
    setIsLoading(false);
  };

  return isLoading ? (
    <CircleProgressComponent variant="indeterminate" />
  ) : (
    <ButtonComponent
      className="csv-report__download-container__button"
      onClick={() => fetchMonthCsv(date)}
      disabled={disabled}
    >
      Download
    </ButtonComponent>
  );
};

const AggregatedCsvReport: FC = () => {
  const lastMonth = dayjs().subtract(1, 'month');

  const [month, setMonth] = useState<number>(lastMonth.month());
  const [year, setYear] = useState<string>(lastMonth.year().toString());
  const [months, setMonths] = useState(dropdownMonths(year));
  const isValidYear =
    Number.isInteger(+year) && Number(year) <= lastMonth.year();
  const canFetch = Number.isInteger(month) && year && isValidYear;

  useEffect(() => {
    setMonths(dropdownMonths(year));
  }, [year]);

  return (
    <>
      <h1 className="csv-report-hero">Aggregated report by month</h1>
      <div className="csv-report">
        <InputComponent
          className="csv-report__year"
          id="csvYear"
          label="Year"
          placeholder="YYYY"
          name="csvYear"
          error={!isValidYear}
          helperText={isValidYear ? null : 'Invalid year value'}
          value={year ?? ''}
          onChange={e => setYear(e.currentTarget.value)}
          fullWidth
        />
        <DropdownComponent
          className="csv-report__dropdown"
          id="csvMonth"
          label="Month"
          labelId="csv-month"
          value={month < months.length ? month : lastMonth.month()}
          onChange={e => setMonth(+e.target.value)}
          itemsToSelect={months}
        />
        <div className="csv-report__download-container">
          <FetchCsvButton
            date={new Date(+year, month, 1)}
            disabled={!canFetch}
          />
        </div>
      </div>
    </>
  );
};

export default AggregatedCsvReport;
